import { ActionTree } from 'vuex';
import { FlowDesignerState } from '.';
import { LOAD_FLOW, LOAD_FLOWS, SAVE_FLOW, UPDATE_STATUS } from './actionTypes';
import { SET_CURRENT_PATH, ADD_PATH } from './paths/mutationTypes';
import { AppState } from '../app';
import {
  SET_CURRENT_FLOW,
  RESET_CHANGE_TRACKER,
  SET_FLOWS,
  SET_FLOW_VARIABLES,
  SET_FLOW_STATUS,
  UPDATE_FLOW,
} from './mutationTypes';
import { UpdateBotFlowViewModel } from '@/features/bots/models/request/updateBotFlowViewModel';
import { HANDLE_SERIOUS_ERROR } from '../app/actionTypes';
import { nanoid } from 'nanoid';
import { InputVariableViewModel, PathViewModel, StateViewModel } from '@/features/bots/models/designer';
import { flowsApi } from '@/features/bots/services/flows';
import { ConditionGroup } from '@/shared/components/conditions/models/conditionGroup';
import { ConditionViewModel } from '@/shared/components/conditions/models/conditionViewModel';
import { ContactAttributeViewModel } from '@/messaging/models/contactAttributeViewModel';
import { BusinessHoursViewModel } from '@/features/bots/models';
import { FlowStatus } from '@/features/bots/models/flowStatus';
import { LOAD_INTENTS } from '@/store/intents/actionTypes';
import { IntentListItemViewModel } from '@/features/bots/models/intents/intentListItemViewModel';

export const actions: ActionTree<FlowDesignerState, AppState> = {
  async [LOAD_FLOW]({ commit, getters, rootState, dispatch }, flowId: string) {
    const currentWorkspace = (rootState as any).app.currentWorkspace;
    if (!currentWorkspace) {
      return dispatch(HANDLE_SERIOUS_ERROR, 'No workspace selected');
    }

    try {
      const flow = await flowsApi.getFlow(currentWorkspace.id, flowId);
      commit(SET_CURRENT_FLOW, flow);
      // Update any storedVariables mapped to contact attributes to have correct name and a data type
      const flowVariables: InputVariableViewModel[] = [];
      flow.paths.forEach((path) => {
        path.states.forEach((state) => {
          getTransitionPayloads(state).forEach((payload) => {
            if (getters.isTransitionPayloadInvalid(payload)) {
              state.invalid = true;
            }
          });
          if (state.storedVariable) {
            if (state.storedVariable.scope !== 'flow') {
              const attributeId = state.storedVariable.key;
              const attribute = getters.getAttribute(attributeId);
              state.storedVariable.dataType = attribute ? attribute.type : undefined;
              state.storedVariable.name = attribute ? attribute.name : state.storedVariable.name;
            } else {
              if (
                flowVariables.find(
                  (v) => v.scope === state.storedVariable!.scope && v.key === state.storedVariable!.key,
                ) === undefined
              ) {
                flowVariables.push(state.storedVariable);
              }
            }
          }
        });
      });
      const intentCondition = flow.triggerConditions
        ? ConditionGroup.find(flow.triggerConditions, (condition: ConditionViewModel) => condition.type === 'intent')
        : null;
      if (intentCondition) {
        const allIntents: IntentListItemViewModel[] = await dispatch(LOAD_INTENTS);
        intentCondition.invalid = !isIntentConditionValid(intentCondition, allIntents);
      }

      ConditionGroup.forEachCondition(flow.audienceConditions, (condition: ConditionViewModel) => {
        if (!condition.invalid) {
          const attribute: ContactAttributeViewModel = getters.getAttribute(
            condition.attribute.substring(condition.attribute.lastIndexOf('.') + 1),
          );
          condition.invalid = !attribute || attribute.archived;
        }
      });
      commit(SET_FLOW_VARIABLES, flowVariables);

      // ensure there's a 'first path'
      let firstPathId = '';
      if (flow.paths.length > 0) {
        firstPathId = flow.paths[0].id;
      } else {
        const firstPath = {
          id: `path-${nanoid(7)}`,
          name: `Welcome`,
          states: [],
        } as PathViewModel;

        commit(ADD_PATH, firstPath);
        firstPathId = firstPath.id;
      }

      commit(RESET_CHANGE_TRACKER);
      commit(SET_CURRENT_PATH, firstPathId);
      return flow;
    } catch (err: any) {
      dispatch(HANDLE_SERIOUS_ERROR, err.message);
    }
  },

  async [SAVE_FLOW]({ state, rootState, dispatch, getters, commit }) {
    const currentWorkspace = (rootState as any).app.currentWorkspace;
    if (!currentWorkspace) {
      return dispatch(HANDLE_SERIOUS_ERROR, 'No workspace selected');
    }

    if (!getters.hasMadeChanges) {
      return Promise.resolve();
    }

    if (getters.isFlowInvalid) {
      throw new Error('The flow is invalid. Fix the problems highlighted and try again');
    }

    const request = {} as UpdateBotFlowViewModel;

    if (getters.hasPathsChanged) {
      request.paths = state.currentFlow.paths;
    }

    if (getters.hasNameChanged) {
      request.name = state.currentFlow.name;
    }

    if (getters.hasTriggersChanged) {
      request.triggers = state.currentFlow.triggers;
    }

    if (getters.hasConditionsChanged) {
      request.triggerConditions = state.currentFlow.triggerConditions;
      request.audienceConditions = state.currentFlow.audienceConditions;
      request.targetContactTypes = state.currentFlow.targetContactTypes;
      if (state.currentFlow.scheduleCondition) {
        request.scheduleCondition = state.currentFlow.scheduleCondition;
      }
      request.businessHours = state.currentFlow.businessHours.map((val: BusinessHoursViewModel) => {
        if (val.closed) {
          val.startTime = undefined;
          val.endTime = undefined;
        }
        return val;
      });
    }

    try {
      const updatedFlow = await flowsApi.updateFlow(
        (rootState as any).app.currentWorkspace.id,
        state.currentFlow.id,
        request,
      );
      commit(UPDATE_FLOW, updatedFlow);
      return updatedFlow;
    } catch (err: any) {
      throw new Error(`Error saving flow: ${err.message}`);
    }
  },

  async [LOAD_FLOWS]({ commit, rootState, dispatch, state }, forceLoad?: boolean) {
    const currentWorkspace = (rootState as any).app.currentWorkspace;
    if (!currentWorkspace) {
      return dispatch(HANDLE_SERIOUS_ERROR, 'No workspace selected');
    }

    if (forceLoad || !state.flows || state.flows.length === 0) {
      try {
        const data = await flowsApi.getFlows(currentWorkspace.id);
        if (data && data.flows) {
          commit(SET_FLOWS, data.flows);
        }
      } catch (err: any) {
        throw new Error(`Error loading flows: ${err.message}`);
      }
    }
    return state.flows;
  },

  async [UPDATE_STATUS]({ commit, rootState, dispatch, state }, status: FlowStatus) {
    const currentWorkspace = (rootState as any).app.currentWorkspace;
    if (!currentWorkspace) {
      return dispatch(HANDLE_SERIOUS_ERROR, 'No workspace selected');
    }
    try {
      await flowsApi.updateFlow(currentWorkspace.id, state.currentFlow.id, { status });
      commit(SET_FLOW_STATUS, status);
    } catch (err: any) {
      throw new Error(`Error updating flow status: ${err.message}`);
    }
  },
};

function isIntentConditionValid(intentCondition: ConditionViewModel, allIntents: IntentListItemViewModel[]): boolean {
  const intentId = intentCondition.value;
  const intent = allIntents.find((i) => i.id === intentId);
  return !!intent;
}

function getTransitionPayloads(state: StateViewModel): Set<string> {
  const flows = new Set<string>();

  if (state.decisions) {
    state.decisions.forEach((decision) => {
      if (decision.transition && decision.transition.payload) {
        flows.add(decision.transition.payload);
      }
    });
  }

  if (state.options) {
    state.options.forEach((option) => {
      if (option.transition && option.transition.payload) {
        flows.add(option.transition.payload);
      }
    });
  }

  if (state.noMatch && state.noMatch.payload) {
    flows.add(state.noMatch.payload);
  }

  if (state.transition && state.transition.payload) {
    flows.add(state.transition.payload);
  }

  return flows;
}
